import { AfterViewInit, Component, ElementRef, Inject, OnInit, Optional, Renderer2, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import CustomValidators from '../PasswordMatcher';
import { DealerState } from '../../../../shared/state/DealerState';
import { AuthenticationService } from '../../../authentication/services/authentication.service';
import { Dealer } from '../../../../shared/dealer/models/dealer';
import { Store } from '@ngxs/store';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserManagementService } from '../../services/user-management.service';
import { UserState } from '../../../../shared/state/UserState';
import { AlertType } from '@michelin/theme';
import { UtilityService } from '../../../../shared/services/utility.service';

@Component({
  selector: 'app-change-password-component',
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  standalone: false
})
export class ChangePasswordComponent implements OnInit, AfterViewInit {
  @Optional() @Inject(MAT_DIALOG_DATA) private readonly dialogData: { username: string; isResetPassword?: boolean } = inject(
    MAT_DIALOG_DATA,
    { optional: true }
  );
  public fb = inject(FormBuilder);
  public renderer = inject(Renderer2);
  public translationService = inject(TranslationService);
  public store = inject(Store);
  public authenticationService = inject(AuthenticationService);
  public router = inject(Router);
  public utilityService = inject(UtilityService);
  @Optional() public dialogRef = inject(MatDialogRef<ChangePasswordComponent>, { optional: true });
  public userManagementService = inject(UserManagementService);
  changePasswordForm: FormGroup;
  dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
  passwordChangeFailed = false;
  passwordChangeProcessing = false;
  @ViewChild('oldPasswordInput') oldPasswordInput: ElementRef;
  @ViewChild('newPasswordInput') newPasswordInput: ElementRef;
  @ViewChild('confirmPasswordInput') confirmPasswordInput: ElementRef;
  currentFocusInput = 'oldPassword';
  hideOld = true;
  hideNew = true;
  hideConfirm = true;
  isFromResetPassword = false;
  userName = '';
  title = 'changePasswordTitleText';
  isLoading = false;
  isFromLogin = false;
  loggedInUsername = '';

  constructor() {
    if (this.dialogData?.username) {
      this.userName = this.dialogData.username;
      this.isFromResetPassword = !!this.dialogData.isResetPassword;
    }

    this.isFromLogin = !!(history.state && history.state.fromLogin);
  }

  ngOnInit() {
    this.loggedInUsername = this.store.selectSnapshot(UserState.getUserName);
    this.changePasswordForm = this.fb.group(
      {
        oldPassword: this.isFromResetPassword ? [''] : ['', Validators.compose([Validators.required])],
        newPassword: ['', Validators.required, CustomValidators.containsSpecialRequirementsValidator()],
        confirmPassword: ['', Validators.compose([Validators.required])]
      },
      {
        validators: [
          CustomValidators.match('newPassword', 'confirmPassword'),
          CustomValidators.newPasswordSameAsOld('confirmPassword', 'oldPassword', 'newPassword')
        ]
      }
    );
  }

  ngAfterViewInit() {
    this.renderer.listen(this.newPasswordInput.nativeElement, 'focus', () => {
      this.currentFocusInput = 'newPassword';
    });
    this.renderer.listen(this.newPasswordInput.nativeElement, 'focus', () => {});
  }

  cancelClicked() {
    if (this.isFromResetPassword) {
      this.isLoading = false;
      this.dialogRef.close('cancel');
    } else if (this.isFromLogin) {
      localStorage.removeItem('token');
      localStorage.removeItem('dealerId');
      localStorage.removeItem('customer');
      localStorage.removeItem('token-expiry');
      localStorage.removeItem('roles');
      localStorage.clear();

      this.router.navigateByUrl('/authlogin');
    } else {
      this.router.navigateByUrl('/home/dashboard');
    }
  }

  async onChangePasswordSubmit(event: Event) {
    event.preventDefault();

    if (this.changePasswordForm.invalid) {
      this.changePasswordForm.markAllAsTouched();
      return;
    }

    const oldPassword = this.isFromResetPassword ? '' : this.changePasswordForm.get('oldPassword').value;
    const newPassword = this.changePasswordForm.get('newPassword').value;
    const username = this.store.selectSnapshot(DealerState.currentUser);
    const dealer: Dealer = this.store.selectSnapshot(DealerState.currentDealer);
    const token = localStorage.getItem('token'); // Retrieve token from localStorage

    if (!token) {
      console.error('Token is missing');
      this.passwordChangeFailed = true;
      return;
    }

    const encryptedPassword = await this.authenticationService.encryptPassword(newPassword, dealer.newHashEnabled);
    const encryptedOldPassword = await this.authenticationService.encryptPassword(oldPassword, dealer.newHashEnabled);
    this.passwordChangeProcessing = true;

    try {
      if (this.isFromResetPassword) {
        this.isLoading = true;
        this.userManagementService.resetPassword(dealer.id, this.userName, encryptedPassword).subscribe(
          (response) => {
            if (response) {
              console.log(response);
            }
            if (this.dialogRef) {
              this.dialogRef.close('success');
            }
            this.passwordChangeFailed = false;
            this.isLoading = false;
          },
          (error: HttpErrorResponse) => {
            this.isLoading = false;
            if (error.status === 401) {
              this.dialogRef.close('failure');
            }
          }
        );
      } else {
        this.isLoading = true;
        await this.authenticationService
          .changePassword(username, encryptedPassword, dealer.id, token, encryptedOldPassword, this.loggedInUsername)
          .then(() => {
            this.passwordChangeFailed = false;
            this.isLoading = false;
            this.router.navigateByUrl('/home/dashboard');
          });
      }
    } catch (error) {
      const httpError = error as HttpErrorResponse;
      console.error(this.translationService.translate('changePasswordErrorText'), error);
      this.isLoading = false;
      if (this.isFromResetPassword) {
        this.dialogRef.close('failure');
      } else {
        if (httpError.status === 401 && httpError.error === 'Old password does not match') {
          this.changePasswordForm.get('oldPassword').setErrors({ incorrectOldPassword: true });
        } else {
          let errorMessage = this.translationService.translate('changePasswordErrorText');
          this.utilityService.showAlert(AlertType.critical, '', errorMessage);
          this.passwordChangeFailed = true;
        }
      }
    } finally {
      this.passwordChangeProcessing = false;
    }
  }

  checkMinLength(fieldName: string) {
    if (this.changePasswordForm.get(fieldName).value.length < 10 && this.changePasswordForm.get(fieldName).value !== '') {
      this.changePasswordForm.get(fieldName).markAsTouched();
      return 'red';
    }
    return this.changePasswordForm.get(fieldName).value === '' ? '#7f7f7f' : 'green';
  }

  checkPasswordCriteriaUpper(password: string) {
    if (/[A-Z]/.test(this.changePasswordForm.get(password).value)) {
      return 'green';
    } else if (
      !this.changePasswordForm.get(password).hasError('containsSpecialRequirements') &&
      this.changePasswordForm.get(password).value !== ''
    ) {
      this.changePasswordForm.get(password).markAsTouched();
      return 'red';
    }
    return 'black';
  }

  checkPasswordCriteriaLower(password: string) {
    if (/[a-z]/.test(this.changePasswordForm.get(password).value)) {
      return 'green';
    } else if (
      !this.changePasswordForm.get(password).hasError('containsSpecialRequirements') &&
      this.changePasswordForm.get(password).value !== ''
    ) {
      this.changePasswordForm.get(password).markAsTouched();
      return 'red';
    }
    return 'black';
  }

  checkPasswordCriteriaNumber(password: string) {
    if (/\d/.test(this.changePasswordForm.get(password).value)) {
      return 'green';
    } else if (
      !this.changePasswordForm.get(password).hasError('containsSpecialRequirements') &&
      this.changePasswordForm.get(password).value !== ''
    ) {
      this.changePasswordForm.get(password).markAsTouched();
      return 'red';
    }
    return 'black';
  }

  checkPasswordCriteriaSpecial(password: string) {
    if (/[~`!@#$%^&*()\-_=+{}[\]|\\;:"<>,./?]/.test(this.changePasswordForm.get(password).value)) {
      return 'green';
    } else if (
      !this.changePasswordForm.get(password).hasError('containsSpecialRequirements') &&
      this.changePasswordForm.get(password).value !== ''
    ) {
      this.changePasswordForm.get(password).markAsTouched();
      return 'red';
    }
    return 'black';
  }
}
